// Shared Gemini API call and response parsing logic for all chatbot contexts
import { API_CONFIG, AI_CONFIG } from '../config';

export interface GeminiResponse {
  json: any; // Parsed JSON object from Gemini response
  naturalLanguage: string; // Natural language explanation from Gemini response
}

export async function callGeminiAPI(prompt: string, apiKey: string): Promise<GeminiResponse> {
  const API_BASE_URL = API_CONFIG.GEMINI.ENDPOINT;

  const response = await fetch(`${API_BASE_URL}?key=${apiKey}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      contents: [
        {
          role: 'user',
          parts: [{ text: prompt }],
        },
      ],
      generationConfig: AI_CONFIG.GEMINI.GENERATION_CONFIG,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Gemini API Error:', errorData);
    throw new Error(`API error: ${errorData.error?.message || 'Unknown error'}`);
  }

  const data = await response.json();
  // Gemini 2.5 Flash may return multiple parts (thinking + text).
  // Filter to only non-thought parts and concatenate.
  const parts = data.candidates?.[0]?.content?.parts || [];
  const textResponse = parts
    .filter((p: any) => !p.thought)
    .map((p: any) => p.text || '')
    .join('');
  // Extract JSON and natural language parts
  // Try fenced JSON first (```json ... ```)
  let jsonMatch = textResponse.match(/```json\s*\n?([\s\S]*?)\n?\s*```/);
  let naturalLanguage = '';

  if (jsonMatch) {
    const afterFence = textResponse.match(/```json\s*\n?[\s\S]*?\n?\s*```\s*([\s\S]*)/);
    naturalLanguage = afterFence ? afterFence[1].trim() : '';
  } else {
    // No fenced JSON — try to find a raw JSON object in the response
    const rawJsonMatch = textResponse.match(/(\{[\s\S]*\})/);
    if (rawJsonMatch) {
      // Everything before the JSON is natural language (prefix)
      const jsonStart = textResponse.indexOf(rawJsonMatch[1]);
      const beforeJson = textResponse.slice(0, jsonStart).trim();
      const afterJson = textResponse.slice(jsonStart + rawJsonMatch[1].length).trim();
      naturalLanguage = (afterJson || beforeJson).trim();
      jsonMatch = rawJsonMatch;
    }
  }

  if (!jsonMatch) {
    throw new Error('Could not parse JSON response from Gemini');
  }

  const json = JSON.parse(jsonMatch[1]);

  // Remove "Natural language response:" prefix if present
  if (naturalLanguage.startsWith('Natural language response:')) {
    naturalLanguage = naturalLanguage.replace('Natural language response:', '').trim();
  }
  // Remove any remaining quotes if present
  if (naturalLanguage.startsWith('"') && naturalLanguage.endsWith('"')) {
    naturalLanguage = naturalLanguage.slice(1, -1);
  }

  return {
    json,
    naturalLanguage,
  };
} 